import { html, type TemplateResult } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { indexesOf } from '../../lib/string.no-deps'

// Helper type used in `replaceTokensWithLitHtml`.
interface ExpandedTokenIndex {
    indexInString: number
    token: string
}

// Helper type used in `replaceTokensWithLitHtml`.
interface TokenReplaceResult {
    lastIndexInString: number
    template: TemplateResult
}

/**
 * For a given list of tokens, replace each token found in the input string
 * with the result of the `toHtml` function.
 *
 * This is useful when you want to replace specific portions of a string
 * (for example a translation), with a more complex lit HTML element.
 *
 * For any given token, `toHtml` will be called with the token.
 *
 * @param inputStr the string to replace tokens in - NOTE that non-token parts will be replaced with `unsafeHTML`.
 * @param tokens a list of tokens to look for the input string
 * @param toHtml a function that will be called for each token found
 * @returns lit html of the input string with the tokens replaced by elements
 */
export function replaceTokensWithLitHtml(
    inputStr: string,
    tokens: string[],
    toHtml: (token: string) => TemplateResult
): TemplateResult {
    // Get an object with the indexes of each token where they occur in the string
    const tokenIndexes = indexesOf(inputStr, tokens)

    // Expand the tokenIndexes object into a list of individual objects where
    // each object contains the token, the index of the token in the input string
    // as well as the index of the token in the array of tokens.
    const { lastIndexInString, template } = Object.entries(tokenIndexes)
        .reduce<ExpandedTokenIndex[]>((acc, [token, indexesInString]) => {
            acc.push(...indexesInString.map((indexInString) => ({ indexInString, token })))

            return acc
        }, [])
        // We need the token+indexes sorted by the index in the input string.
        .sort((a, b) => a.indexInString - b.indexInString)
        // Iterate over the list of token+indexes and replace the non-token part using `unsafeHTML`
        // and the token part with the result from `toHtml`, adding the results to a single template.
        // Also keep track of the last index in the string we have processed, which will also be needed at the end.
        .reduce<TokenReplaceResult>(
            (acc, { indexInString, token }) => {
                const beforeTokenText = inputStr.slice(acc.lastIndexInString, indexInString)
                const beforeTokenElement = html`${unsafeHTML(beforeTokenText)}`
                const tokenElement = toHtml(token)

                acc.template = html`${acc.template}${beforeTokenElement}${tokenElement}`
                acc.lastIndexInString = indexInString + token.length

                return acc
            },
            { lastIndexInString: 0, template: html`` }
        )

    // Add remaining string chunk after the last token at the end.
    return html`${template}${unsafeHTML(inputStr.slice(lastIndexInString))}`
}

/**
 * Adds a translated coordinating conjunction between last two items in a list.
 * Each item is mapped to lit `TemplateResult` using the `toHtml` function.
 *
 * Examples:
 * - ['a', 'b', 'c'] => 'a, b and c'
 * - ['a', 'b'] => 'a and b'
 * - ['a'] => 'a'
 *
 * @param items list of items to join
 * @param toHtml function to map each item to a `TemplateResult`
 * @returns a jointed `TemplateResult` with the items and conjunctions
 */
export function languageSensitiveListFormatter<T>(items: T[], toHtml: (item: T) => TemplateResult): TemplateResult {
    if (items.length === 0) {
        return html``
    }

    if (items.length === 1) {
        return toHtml(items[0])
    }

    return items.reduce(
        (acc, item, i) => {
            if (i < items.length - 2) {
                return html`${acc}${toHtml(item)}, `
            }

            if (i === items.length - 2) {
                const lastConjuction = window.i18n('shared:common.and')

                return html`${acc} ${toHtml(item)} ${lastConjuction}`
            }

            return html`${acc} ${toHtml(item)}`
        },
        html``
    )
}
